import { useEffect } from "react";
import {
  Download,
  Home,
  AlertTriangle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { ProgressBar } from "../ProgressBar";
import { logEvent } from "@shared/analytics/analytics";
import { useLocation } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";

const MigrationReport = ({
  migrationData,
  errorLogs,
  onReturnToDashboard,
  onDownloadErrorReport,
  uploadId: propUploadId,
}) => {
  const {
    task_id,
    data_upload_id,
    dataType = "Data",
    status = "completed",
    processed = 0,
    total = 0,
    completionPercentage = 0,
    processingTime = "0m 0s",
    successRate = 0,
    errorRate = 0,
  } = migrationData || {};
  const { state } = useLocation();
  const uploadId = propUploadId || state?.uploadId;

  const hasErrors = errorLogs && errorLogs.length > 0;
  const successfulRecords = Math.round((successRate / 100) * total);
  const errorCount = errorLogs?.length || 0;
  useEffect(() => {
    if (errorRate) {
      logEvent("Data Upload Errors", {
        errorRate,
      });
    }
  });
  // Get current timestamp for report
  const reportTimestamp = new Date().toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  // Group errors by type for analysis
  const errorTypes =
    errorLogs?.reduce((acc, error) => {
      const errorType = error.errorMsg || "Unknown Error";
      acc[errorType] = (acc[errorType] || 0) + 1;
      return acc;
    }, {}) || {};

  return (
    <div className="min-h-screen w-full bg-gray-50 p-6">
      <ProgressBar
        currentStep={7}
        totalSteps={7}
        progress={100}
        currentStepName={"Migration Report"}
      />

      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-8">
          <PageHeader
            title="Migration Report"
            subtitle="Summary of your data migration process"
          />
        </div>

        {/* Report Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          {/* Migration Header */}
          <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {dataType} Migration
              </h2>
              <p className="text-sm text-gray-600">{reportTimestamp}</p>
              {task_id && (
                <p className="text-xs text-gray-500 mt-1">Task ID: {task_id}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  status === "completed"
                    ? hasErrors
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {status === "completed" && hasErrors ? (
                  <>
                    <AlertTriangle size={14} className="mr-1" />
                    completed with errors
                  </>
                ) : status === "completed" ? (
                  <>
                    <CheckCircle size={14} className="mr-1" />
                    completed
                  </>
                ) : (
                  <>
                    <Clock size={14} className="mr-1" />
                    {status}
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-600 mb-1">
                Total Records
              </div>
              <div className="text-2xl font-bold text-gray-900">{total}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-600 mb-1">
                Success Rate
              </div>
              <div className="text-2xl font-bold text-green-600">
                {successRate}%
              </div>
              <div className="text-xs text-green-600">
                {successfulRecords} records
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-600 mb-1">
                Error Rate
              </div>
              <div className="text-2xl font-bold text-red-600">
                {errorRate}%
              </div>
              <div className="text-xs text-red-600">{errorCount} records</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-600 mb-1">
                Processing Time
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {processingTime}
              </div>
              <div className="text-xs text-blue-600">{processed} processed</div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Success/Error Distribution */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Success/Error Distribution
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">
                      SUCCESS
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">{successRate}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-green-500 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(successRate, 100)}%` }}
                  />
                </div>

                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">
                      ERROR
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">{errorRate}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-red-500 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(errorRate, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4">
            <Button onClick={onReturnToDashboard} className="outline">
              <Home size={16} />
              <span>Return to Dashboard</span>
            </Button>

            <Button onClick={onDownloadErrorReport}>
              <Download size={16} />
              <span>Download Error Report</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigrationReport;
